import { GridService, IGridService } from '../shared/grid/grid.service';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { EvidencePackRequest } from '../models/evidencePackRequest.model';
import { Injectable } from '@angular/core';
import { BulkUpdateSampleStatusResponse, Sample, SampleHistory } from '../models/sample.model';
import { GridModel } from '../shared/grid/grid.model';
import { catchError, Observable, throwError } from 'rxjs';
import { IhLabResult } from '../models/IhLabResult.model';
import { ColumnDetails } from '../models/columnDetails.model';
import { SampleStatusSummaryResponse } from '../models/sample-status-summary.model';
import { ExceedanceComplianceRequest, ExceedanceComplianceResponse } from '../models/exceedance-compliance.model';
import nameOf from '../shared/form/nameOf.helper';
import { SampleStatusEnum } from '../enums/sampleStatus.enum';

export interface SampleStatusSummaryRequest {
    filterBy?: string;
    selectedOrganizationLevelIds?: string[];
    selectedStatuses?: SampleStatusEnum[];
}

export interface BulkUpdateSampleStatusRequest {
    sampleIds: string[];
    status: SampleStatusEnum;
    voidReason?: string;
}

@Injectable({
    providedIn: 'root'
})

export class SampleService extends GridService<Sample> implements IGridService<Sample> {

    constructor(http: HttpClient) {
        super('Sample', http);
    }

    get(filter: string, orderBy: string, sort: string, page: number, pageSize: number, filterBy: string, headers?: HttpHeaders): Observable<GridModel<Sample>> {
        return this.http.get<GridModel<Sample>>(this.getApiUrl(`${this.controllerName}/GetSamples`), {
            params: this.createParamsWithFilterBy(filter, orderBy, sort, page, pageSize, filterBy),
            headers: headers
        });
    }

    override update(payload: Sample): Observable<Sample> {
        return this.http.post<Sample>(this.getApiUrl(`${this.controllerName}/UpdateSample`), payload);
    }

    getHistory(params: HttpParams): Observable<GridModel<SampleHistory>> {
        return this.http.get<GridModel<SampleHistory>>(this.getApiUrl(`${this.controllerName}/GetHistory`), {
            params: params
        });
    }

    getHistoryFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
        return this.http.get<{ [property: string]: ColumnDetails }>(
            this.getApiUrl(`${this.controllerName}/GetHistoryFieldConfiguration`)
        ).pipe(
            catchError((error) => {
                return throwError(() => error);
            })
        );
    }

    getSampleById(id: any): Observable<Sample> {
        return this.http.get<Sample>(this.getApiUrl(`${this.controllerName}/GetSample?id=${id}`));
    }

    getLabResults(sampleId: any): Observable<IhLabResult[]> {
        return this.http.get<IhLabResult[]>(this.getApiUrl(`${this.controllerName}/GetLabResults?sampleId=${sampleId}`));
    }

    createSample(payload: Sample): Observable<Sample> {
        return this.http.post<Sample>(this.getApiUrl(`${this.controllerName}/CreateSample`), payload);
    }

    getStatusSummary(request: SampleStatusSummaryRequest): Observable<SampleStatusSummaryResponse> {
        return this.http.post<SampleStatusSummaryResponse>(
            this.getApiUrl(`${this.controllerName}/GetSampleStatusSummary`),
            request
        );
    }

    getExceedanceCompliance(request: ExceedanceComplianceRequest): Observable<ExceedanceComplianceResponse> {
        let params = new HttpParams();
        const scalarRequest = request as Record<string, string | number | boolean | undefined>;
        const scalarFields = [
            nameOf<ExceedanceComplianceRequest>(x => x.agentId),
            nameOf<ExceedanceComplianceRequest>(x => x.oelTypeId),
            nameOf<ExceedanceComplianceRequest>(x => x.timePeriod),
            nameOf<ExceedanceComplianceRequest>(x => x.year),
            nameOf<ExceedanceComplianceRequest>(x => x.segId),
            nameOf<ExceedanceComplianceRequest>(x => x.surveyTypeId),
            nameOf<ExceedanceComplianceRequest>(x => x.sampleCategory),
            nameOf<ExceedanceComplianceRequest>(x => x.organizationLevelId),
            nameOf<ExceedanceComplianceRequest>(x => x.jurisdictionId),
            nameOf<ExceedanceComplianceRequest>(x => x.sampleType),
            nameOf<ExceedanceComplianceRequest>(x => x.startDate),
            nameOf<ExceedanceComplianceRequest>(x => x.endDate),
        ];
        for (const field of scalarFields) {
            if (scalarRequest[field] != null) {
                params = params.set(field, scalarRequest[field] as string | number | boolean);
            }
        }
        if (request.agentIds?.length) {
            request.agentIds.forEach(id => { params = params.append(nameOf<ExceedanceComplianceRequest>(x => x.agentIds), id); });
        }
        if (request.agentGroupIds?.length) {
            request.agentGroupIds.forEach(id => { params = params.append(nameOf<ExceedanceComplianceRequest>(x => x.agentGroupIds), id); });
        }
        if (request.surveyTypeIds?.length) {
            request.surveyTypeIds.forEach(id => { params = params.append(nameOf<ExceedanceComplianceRequest>(x => x.surveyTypeIds), id); });
        }
        return this.http.get<ExceedanceComplianceResponse>(
            this.getApiUrl(`${this.controllerName}/GetExceedanceCompliance`),
            { params }
        );
    }

    bulkUpdateSampleStatus(sampleIds: string[], newStatus: SampleStatusEnum, voidReason?: string): Observable<BulkUpdateSampleStatusResponse> {
        const payload: BulkUpdateSampleStatusRequest = {
            sampleIds,
            status: newStatus
        };
        if (voidReason) {
            payload.voidReason = voidReason;
        }
        return this.http.post<BulkUpdateSampleStatusResponse>(
            this.getApiUrl(`${this.controllerName}/BulkUpdateSampleStatus`),
            payload
        );
    }
    unlinkFromCoc(sampleId: string): Observable<Sample> {
        return this.http.patch<Sample>(
            this.getApiUrl(`${this.controllerName}/UnlinkCoc?id=${sampleId}`),
            {}
        ).pipe(
            catchError((error) => {
            return throwError(() => error);
            })
        );
    }

    markLetterSent(sampleIds: string[]): Observable<void> {
        return this.http.post<void>(this.getApiUrl(`${this.controllerName}/MarkLetterSent`), { sampleIds });
    }

    exportEvidencePack(request: EvidencePackRequest): Observable<HttpResponse<Blob>> {
        return this.http.post(this.getApiUrl(`${this.controllerName}/ExportEvidencePack`), request, {
            responseType: 'blob',
            observe: 'response'
        });
    }
}
